import { Box, CardContent, Divider, Stack } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { GcpEncryptionComponent } from '@/components/encryption/gcpEncryptionComponent';
import { AvailabilityZoneSelect } from '@/components/regions/availabilityZoneSelect';
import { RegionSelect } from '@/components/regions/regionSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import { DiskSettingsSection } from '@/components/volumes/gcpDiskSettings';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { StepContainer } from '../../../wizard/StepContainer';
import type { RestoreGcpDiskState } from '../../restoreGcpDiskWizard';
import { EonTagRemark } from '../eonTagRemark';

export const GcpDiskConfiguration = (props: StepProps<RestoreGcpDiskState>) => {
  const [isRestoring, setIsRestoring] = useState(false);
  const [keepOriginalSettings, setKeepOriginalSettings] = useState(true);
  const [diskSettings, setDiskSettings] = useState({
    ...props.wizardState.originalDiskSettings,
  });
  const [error, setError] = useState<string | undefined>();
  const [customTags, setCustomTags] = useState({
    ...props.wizardState.originalTags,
  });
  const [keepOriginalTags, setKeepOriginalTags] = useState(true);
  const dal = useDAL();

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const labels = {
      ...(keepOriginalTags ? props.wizardState.originalTags : customTags),
      'eon-restore': 'true',
    };

    return dal.restore.gcpVm
      .restoreDisk(
        props.wizardState.resourceId,
        props.wizardState.snapshot.id,
        {
          restoreAccountId: props.wizardState.restoreAccount!.id,
          diskId: props.wizardState.originalDiskSettings.diskId,
          diskName: diskSettings.diskName,
          diskType: diskSettings.diskType,
          diskSizeBytes: diskSettings.diskSizeBytes,
          zone: props.wizardState.zone!,
          labels,
          encryptionKeyId: props.wizardState.encryptionKey,
          iops: diskSettings.iops,
          throughput: diskSettings.throughput,
        }
      )
      .then(() => props.finish('Restore job has started!'))
      .catch((e) => {
        setIsRestoring(false);
        setError(`Failed to restore disk: ${e.message}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          isRestoring ||
          !props.wizardState.regionName ||
          !props.wizardState.zone
        ) {
          return false;
        }

        const settings = keepOriginalSettings
          ? props.wizardState.originalDiskSettings
          : diskSettings;

        return (
          !!settings?.diskName &&
          !!settings?.diskType &&
          !!settings.diskSizeBytes
        );
      }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark provider={Provider.Gcp} />
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <RegionSelect
            cloudProvider={Provider.Gcp}
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(regionName) =>
              props.setWizardState((state) => ({
                ...state,
                regionName,
              }))
            }
          />
          <AvailabilityZoneSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            availabilityZone={props.wizardState.zone}
            cloudProvider={Provider.Gcp}
            onChange={(zone) =>
              props.setWizardState((state) => ({
                ...state,
                zone,
              }))
            }
          />
        </Stack>
      </Box>
      <Divider />
      <GcpEncryptionComponent
        cloudAccountId={props.wizardState.restoreAccount?.id}
        regionName={props.wizardState.regionName}
        onChange={(encryptionKey) => {
          props.setWizardState((state) => ({
            ...state,
            encryptionKey,
          }));
        }}
        canBeTurnedOff={true}
        title='Encrypt this resource'
        initialEncryptionKeyName={props.wizardState.encryptionKey}
      />
      <Divider />
      <CardContent className='p-0'>
        <DiskSettingsSection
          initialSettings={props.wizardState.originalDiskSettings}
          settings={diskSettings}
          accountId={props.wizardState.restoreAccount?.id ?? ''}
          zone={props.wizardState.zone ?? ''}
          keepOriginalSettings={keepOriginalSettings}
          onChange={setDiskSettings}
          setKeepOriginalSettings={setKeepOriginalSettings}
          machineType={props.wizardState.machineType}
        />
      </CardContent>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.originalTags}
          tags={customTags}
          keepOriginalTags={keepOriginalTags}
          setKeepOriginalTags={setKeepOriginalTags}
          onChange={setCustomTags}
        />
      </CardContent>
    </StepContainer>
  );
};
